import TextEditorCustomize from '@/components/TextEditorCustomize'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import CloseIcons from '@/icons/CloseIcons'
import ConfirmServiceIcons from '@/icons/ConfirmServiceIcons'
import {
    rejectInternalTicketWorkflowByStepId,
    resolveInternalTicketWorkflowByStepId,
} from '@/modules/Service/services/ticket'
import {
    FilledData,
    InternalTicketWorkflowStepName,
} from '@/modules/Service/types'
import { IPermissionProps } from '@/types/shared'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import toast from 'react-hot-toast'
import { QUERY_KEYS, QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'

interface FinanceVerificationFormProps extends IPermissionProps {
    stepId: number
    stepName: InternalTicketWorkflowStepName
    filledData?: FilledData
}

const FinanceVerificationForm: React.FC<FinanceVerificationFormProps> = ({
    stepId,
    stepName,
    filledData,
}) => {
    const { t } = useTranslation()
    const [showRejectForm, setShowRejectForm] = useState(false)
    const { refetch } = useQueryClientCallback({
        pattern: 'tickets',
        mode: 'contains',
    })
    const { refetch: refetchServices } = useQueryClientCallback({
        pattern: 'services',
        mode: 'contains',
    })

    const { mutateAsync: resolveStep, isPending: isResolving } =
        useMutationWithHandler(
            { mutationFn: resolveInternalTicketWorkflowByStepId },
            {
                successMessage: t('step-approved-success'),
                reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_TICKET,
                onSuccess: () => {
                    refetch()
                    refetchServices()
                },
            }
        )

    const { mutateAsync: rejectStep, isPending: isRejecting } =
        useMutationWithHandler(
            {
                mutationFn: async ({
                    stepId,
                    reason,
                }: {
                    stepId: number
                    reason: string
                }) => {
                    return rejectInternalTicketWorkflowByStepId({
                        stepId,
                        reason,
                    })
                },
            },
            {
                successMessage: t('step-rejected-success'),
                reactQueryKeys: QUERY_KEYS.TICKETS,
                onSuccess: () => {
                    refetch()
                    setShowRejectForm(false)
                    setRejectReason('')
                },
            }
        )

    const [rejectReason, setRejectReason] = useState('')

    const handleApprove = async () => {
        await resolveStep({ stepId })
    }

    const handleReject = async () => {
        if (!rejectReason.trim()) {
            toast.error(t('reject-reason-required'))
            return
        }
        await rejectStep({ stepId, reason: rejectReason })
    }

    const isPending = isResolving || isRejecting

    const getStepConfig = () => {
        switch (stepName) {
            case InternalTicketWorkflowStepName.FinanceApproveTaxPercentageUpdate:
                return {
                    title: t('finance-approve-service-tax-percentage-title'),
                    description: t(
                        'finance-approve-service-tax-percentage-description'
                    ),
                    bgColor: 'bg-indigo-50',
                    borderColor: 'border-indigo-200',
                    titleColor: 'text-indigo-900',
                    descColor: 'text-indigo-700',
                }
            default:
                return {
                    title: t('step-completion-title'),
                    description: t('step-completion-description'),
                    bgColor: 'bg-gray-50',
                    borderColor: 'border-gray-200',
                    titleColor: 'text-gray-900',
                    descColor: 'text-gray-700',
                }
        }
    }

    const stepConfig = getStepConfig()

    return (
        <div className="space-y-2">
            <div
                className={`p-4 ${stepConfig.bgColor} border ${stepConfig.borderColor} rounded-md`}
            >
                <Typogaphy
                    className={`font-medium ${stepConfig.titleColor} mb-2`}
                >
                    {stepConfig.title}
                </Typogaphy>
                <Typogaphy className={`text-xs ${stepConfig.descColor} mb-4`}>
                    {stepConfig.description}
                </Typogaphy>

                {/* Display FilledData Information */}
                {filledData && (
                    <div className="mb-4 space-y-2 bg-white border border-gray-200 rounded-md p-3">
                        <Typogaphy className="font-medium text-gray-800 text-sm mb-2">
                            {t('request-details')}
                        </Typogaphy>

                        {filledData.service_tax_percentage !== undefined && (
                            <div className="grid grid-cols-3 gap-2">
                                <Typogaphy className="text-xs text-gray-600 font-medium">
                                    {t('service-tax-percentage')}:
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-800 col-span-2">
                                    {filledData.service_tax_percentage}%
                                </Typogaphy>
                            </div>
                        )}

                        {filledData.note && (
                            <div className="grid grid-cols-3 gap-2">
                                <Typogaphy className="text-xs text-gray-600 font-medium">
                                    {t('note')}:
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-800 col-span-2 whitespace-pre-wrap">
                                    {filledData.note}
                                </Typogaphy>
                            </div>
                        )}
                    </div>
                )}

                {!showRejectForm ? (
                    <div className="flex flex-col md:flex-row gap-2 justify-end w-full">
                        <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            className="border-green-500"
                            startIcon={
                                <ConfirmServiceIcons className="w-4 h-4" />
                            }
                            onClick={handleApprove}
                            loading={isResolving}
                            disabled={isPending}
                        >
                            {t('approve-and-complete')}
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<CloseIcons className="w-4 h-4" />}
                            onClick={() => setShowRejectForm(true)}
                            disabled={isPending}
                        >
                            {t('reject')}
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <TextEditorCustomize
                            label={t('reject-reason')}
                            value={{
                                text: rejectReason,
                                attachments: [],
                            }}
                            className="bg-white rounded-md"
                            onChange={(e: any) => setRejectReason(e.text)}
                            placeholder={t('enter-reject-reason')}
                        />
                        <div className="flex flex-col md:flex-row gap-2 justify-end w-full">
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={handleReject}
                                loading={isRejecting}
                                disabled={isPending || !rejectReason.trim()}
                            >
                                {t('confirm-reject')}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                    setShowRejectForm(false)
                                    setRejectReason('')
                                }}
                                disabled={isPending}
                            >
                                {t('cancel')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default FinanceVerificationForm
